import {Injectable} from '@angular/core';
import {UserService} from '../../../core/services';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {Ticket} from '../../../api/model/Ticket.model';
import {TjRxStompService} from '../../shared/services/tj-rx-stomp.service';
import {Message} from '@stomp/stompjs';
import {User} from '../../../api/model/User.model';
import {ApiService} from '../../../api/service/api.service';
import {filter, first} from 'rxjs/operators';
import {RxStompState} from '@stomp/rx-stomp';

export interface PresenceMessage {
  ticketId: number;
  user: PresenceUser;
  joined: boolean;
}

export interface PresenceUser {
  id: number;
  name: string;
  username: string;
}

@Injectable({
  providedIn: 'root',
})
export class TicketPresenceCheckService {
  private topicSubscription: Subscription;
  private activeUsers = new Set<string>();
  private presenceSubject = new BehaviorSubject<string[]>([]);
  public presence$: Observable<string[]> = this.presenceSubject.asObservable();

  constructor(
    private api: ApiService,
    private rxStompService: TjRxStompService,
    private userService: UserService,
  ) {}

  initActiveViewers(ticket: Ticket) {
    this.api.ticket.presence(ticket.id, true).subscribe({
      next: response => {
        const ticketViewUsers = response.users.map(user => user.name);
        this.presenceSubject.next(ticketViewUsers);
      },
      error: err => {
        console.error('Error during presence update', err);
      },
    });
  }

  public initTicketPresenceTracking(ticket: Ticket): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.fetchCurrentUser().subscribe({
        next: (user: User) => {
          this.rxStompService.configure({
            connectHeaders: {
              realm: user.realm,
              ticketId: ticket.id.toString(),
              name: user.name,
              username: user.username,
            },
          });
          this.rxStompService.activate();

          // Wait until WebSocket is fully connected
          this.rxStompService.connectionState$
            .pipe(
              filter(state => state === RxStompState.OPEN),
              first(),
            )
            .subscribe(() => {
              this.topicSubscription = this.rxStompService
                .watch('/topic/' + user.realm + '/ticket-presence/' + ticket.id)
                .subscribe((message: Message) => {
                  const data: PresenceMessage = JSON.parse(message.body);

                  this.activeUsers = new Set<string>(this.presenceSubject.getValue());

                  if (data.ticketId !== ticket.id) {
                    return;
                  }

                  if (user.name === data.user.name) {
                    return;
                  }

                  if (data.joined) {
                    this.activeUsers.add(data.user.name);
                  } else {
                    this.activeUsers.delete(data.user.name);
                  }

                  this.presenceSubject.next(Array.from(this.activeUsers));
                });

              resolve();
            });
        },
        error: err => reject(err),
      });
    });
  }

  updatePresence(ticketId: number, presence: boolean) {
    this.api.ticket.presence(ticketId, presence).subscribe();
  }

  public destroy(): void {
    this.rxStompService.deactivate().then();
    if (this.topicSubscription) {
      this.topicSubscription.unsubscribe();
    }
    this.activeUsers.clear();
    this.presenceSubject.next([]);
  }
}
